import type {
  AmhsLiveInventory,
  AmhsLiveInventoryPort,
  AmhsLiveInventoryStocker,
} from '../../types/amhsStream';

export type StockerInventoryChange = {
  stockerId?: string;
  change?: string;
  column?: string;
  columnOccupancy?: number;
  occupancy?: number;
  ports?: AmhsLiveInventoryPort[];
};

function normalizeColumnKind(value: unknown): 'raw' | 'processed' | undefined {
  const kind = String(value ?? '')
    .trim()
    .toLowerCase();
  if (kind === 'raw' || kind === 'new') return 'raw';
  if (kind === 'processed' || kind === 'finished') return 'processed';
  return undefined;
}

function portOccupancy(port: AmhsLiveInventoryPort): number {
  if (typeof port.totalInventory === 'number' && Number.isFinite(port.totalInventory)) {
    return port.totalInventory;
  }
  if (typeof port.availableInventory === 'number' && Number.isFinite(port.availableInventory)) {
    return port.availableInventory;
  }
  return 0;
}

function mergePorts(
  existing: readonly AmhsLiveInventoryPort[] | undefined,
  updates: readonly AmhsLiveInventoryPort[] | undefined
): AmhsLiveInventoryPort[] | undefined {
  if (!updates?.length) return existing ? [...existing] : undefined;
  const byId = new Map<string, AmhsLiveInventoryPort>();
  for (const port of existing ?? []) {
    if (port.loadPortId) byId.set(port.loadPortId, { ...port });
  }
  for (const port of updates) {
    if (!port.loadPortId) continue;
    byId.set(port.loadPortId, { ...byId.get(port.loadPortId), ...port });
  }
  return [...byId.values()];
}

function columnTotalsFromPorts(
  ports: readonly AmhsLiveInventoryPort[] | undefined
): { raw: number; processed: number } | undefined {
  if (!ports?.length) return undefined;
  let raw = 0;
  let processed = 0;
  for (const port of ports) {
    const kind = normalizeColumnKind(port.column);
    const count = portOccupancy(port);
    if (kind === 'raw') raw += count;
    else if (kind === 'processed') processed += count;
  }
  return { raw, processed };
}

function withColumnCounts(
  stocker: AmhsLiveInventoryStocker,
  raw: number,
  processed: number
): AmhsLiveInventoryStocker {
  const nextRaw = Math.max(0, raw);
  const nextProcessed = Math.max(0, processed);
  const nextColumns = stocker.columns?.map((column) => {
    const kind = normalizeColumnKind(column.kind);
    if (kind === 'raw') {
      return { ...column, occupancy: nextRaw, free: Math.max(0, column.capacity - nextRaw) };
    }
    if (kind === 'processed') {
      return {
        ...column,
        occupancy: nextProcessed,
        free: Math.max(0, column.capacity - nextProcessed),
      };
    }
    return column;
  });
  return {
    ...stocker,
    rawOccupancy: nextRaw,
    newOccupancy: nextRaw,
    processedOccupancy: nextProcessed,
    occupancy: nextRaw + nextProcessed,
    ...(nextColumns ? { columns: nextColumns } : {}),
  };
}

function applyColumnOccupancy(
  stocker: AmhsLiveInventoryStocker,
  column: 'raw' | 'processed',
  occupancy: number
): AmhsLiveInventoryStocker {
  const raw = column === 'raw' ? occupancy : (stocker.rawOccupancy ?? stocker.newOccupancy ?? 0);
  const processed =
    column === 'processed' ? occupancy : (stocker.processedOccupancy ?? 0);
  return withColumnCounts(stocker, raw, processed);
}

/**
 * Apply one streamed `stocker_inventory` row to the replica table.
 * Mirrors `scripts/check_stream_stocker_inventory.py::apply_change`.
 */
export function applyLiveStockerInventoryChange(
  inventory: AmhsLiveInventory | null | undefined,
  change: StockerInventoryChange
): AmhsLiveInventory | null {
  if (!inventory || !change.stockerId || change.change === 'reserved') {
    return inventory ?? null;
  }

  const stockers = (inventory.stockers ?? []).map((row) => ({ ...row }));
  const index = stockers.findIndex((row) => row.stockerId === change.stockerId);
  const existing = index >= 0 ? stockers[index]! : ({ stockerId: change.stockerId } as AmhsLiveInventoryStocker);

  let next: AmhsLiveInventoryStocker = {
    ...existing,
    ports: mergePorts(existing.ports, change.ports),
  };

  const fromPorts = columnTotalsFromPorts(next.ports);
  if (fromPorts) {
    next = withColumnCounts(next, fromPorts.raw, fromPorts.processed);
  }

  const columnKind = normalizeColumnKind(change.column);
  if (columnKind && typeof change.columnOccupancy === 'number') {
    next = applyColumnOccupancy(next, columnKind, change.columnOccupancy);
  }

  if (typeof change.occupancy === 'number' && Number.isFinite(change.occupancy)) {
    next = { ...next, occupancy: change.occupancy };
  }

  if (index >= 0) stockers[index] = next;
  else stockers.push(next);

  return { ...inventory, stockers };
}

/** Build the inventory object the UI reads from a layout/snapshot slice. */
export function mapStreamInventory(
  inventory: AmhsLiveInventory | null | undefined,
  stockerRows: readonly AmhsLiveInventoryStocker[] | undefined
): AmhsLiveInventory | null {
  const rows =
    inventory?.stockers ??
    (Array.isArray(stockerRows) ? [...stockerRows] : undefined);
  if (!rows?.length) return inventory ?? null;

  const totalStockerOcc = rows.reduce((sum, row) => sum + (row.occupancy ?? 0), 0);
  const rawOcc = rows.reduce(
    (sum, row) => sum + (row.rawOccupancy ?? row.newOccupancy ?? 0),
    0
  );
  const processedOcc = rows.reduce((sum, row) => sum + (row.processedOccupancy ?? 0), 0);

  return {
    ...(inventory ?? {}),
    stockers: rows.map((row) => ({ ...row })),
    stockerOccupancy: inventory?.stockerOccupancy ?? totalStockerOcc,
    rawOccupancy: inventory?.rawOccupancy ?? rawOcc,
    newOccupancy: inventory?.newOccupancy ?? rawOcc,
    processedOccupancy: inventory?.processedOccupancy ?? processedOcc,
    foupsInSystem: inventory?.foupsInSystem,
    foupCapacity: inventory?.foupCapacity,
    gates: inventory?.gates,
    status: inventory?.status,
  };
}
